/**
 * The exercise countdown as a wall-clock state, not a number decremented once a second: a
 * phone freezes a backgrounded app's timers, so someone who switches to a reading app and
 * comes back would find the clock where they left it. While running, only the moment it
 * ends is kept; the time left is read against the clock whenever the screen asks.
 */
export type Countdown = { running: true; endsAt: number } | { running: false; remainingMs: number };

export function startCountdown(totalSeconds: number, now: number): Countdown {
  return { running: true, endsAt: now + totalSeconds * 1000 };
}

/** Whole seconds left, rounded up so the clock shows 10:00 at the start and 00:00 only at the end. */
export function secondsLeft(countdown: Countdown, now: number): number {
  const ms = countdown.running ? countdown.endsAt - now : countdown.remainingMs;
  return Math.max(0, Math.ceil(ms / 1000));
}

export function pauseCountdown(countdown: Countdown, now: number): Countdown {
  if (!countdown.running) return countdown;
  return { running: false, remainingMs: Math.max(0, countdown.endsAt - now) };
}

export function resumeCountdown(countdown: Countdown, now: number): Countdown {
  if (countdown.running) return countdown;
  return { running: true, endsAt: now + countdown.remainingMs };
}

/**
 * Whether to tell the person their time is up: the countdown has reached zero and they have
 * not answered the notice yet. A reset starts a new countdown, whose end is told again.
 */
export function timeUpNoticeOpen(secondsLeftNow: number, acknowledged: boolean): boolean {
  return secondsLeftNow === 0 && !acknowledged;
}

/**
 * The two notices around finishing an exercise: "You did it!" when the countdown reaches
 * zero, and "Don't leave yet" when the person presses Done with time left.
 */
export type FinishNotice = 'time-up' | 'not-yet';

/**
 * Whether "Done" finishes the exercise straight away. The countdown is the day's target:
 * with time left, the person is first asked to keep going, and may still finish.
 */
export function finishesAtOnce(secondsLeftNow: number): boolean {
  return secondsLeftNow === 0;
}

/**
 * The finishing notice to show, if any. `timeUpAnswered`: the person already answered
 * "You did it!" for this countdown. `doneEarly`: they pressed Done with time left and haven't
 * answered "Don't leave yet". The countdown reaching zero while "Don't leave yet" is up turns
 * it into "You did it!", which is then true.
 */
export function finishNotice(
  secondsLeftNow: number,
  { timeUpAnswered, doneEarly }: { timeUpAnswered: boolean; doneEarly: boolean },
): FinishNotice | null {
  if (timeUpNoticeOpen(secondsLeftNow, timeUpAnswered)) return 'time-up';
  return doneEarly && !finishesAtOnce(secondsLeftNow) ? 'not-yet' : null;
}
